import { HeaderIcons } from '../shared/icons';
import React, { useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LiveAnswer as LiveAnswerType, LoadingQuestion, ImageWithFallback } from '../shared';
import { useGameState } from '../../hooks/useGameState';
import { useQuestionTimer } from '../../hooks/useQuestionTimer';
import { useQuestionFlip, useTimerFlipTrigger } from '../../hooks/useQuestionFlip';
import { useGameSocketEvents } from '../../hooks/useGameSocketEvents';
import { usePlayerTracking } from '../../hooks/usePlayerTracking';
import { GameEndScreen } from './GameEndScreen';
import { AnswerOptions } from '../shared';
import { QuestionDisplay, TimerSection } from '../shared';
import { getGameName } from '../../utils/gameUtils';
import { GameDisplayRoomState } from '../../types/game';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';
import { gameUsesTeams } from '../../utils/teamGames';
import { syncPlayersFromLeaderboard } from '../../utils/leaderboardUtils';

interface GameDisplayProps {
  roomState: GameDisplayRoomState;
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  /** Called by LoadingQuestion after 10s with no response */
  onLoadingTimeout?: () => void;
}

const GameDisplay: React.FC<GameDisplayProps> = ({ 
  roomState, 
  socket, 
  onBackToGameSelection, 
  myPlayerId, 
  hostIsPlaying = false, 
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const roomNameRef = useRef<string>(roomState.roomName);
  /** Sync guard — React state alone can miss double-clicks before re-render. */
  const isSubmittingRef = useRef(false);
  
  // Update roomName ref when roomState changes
  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  // Main game state management
  const gameState = useGameState(roomState);

  // Keep ref aligned when submit settles or question resets
  useEffect(() => {
    if (!gameState.hostPlayingState.isSubmitting) {
      isSubmittingRef.current = false;
    }
  }, [gameState.hostPlayingState.isSubmitting]);

  // If submit never gets answer-received / answer-rejected (older BE / dropped packet), unlock button
  useEffect(() => {
    if (!gameState.hostPlayingState.isSubmitting || gameState.hostPlayingState.answerSubmitted) {
      return;
    }
    const timeoutId = setTimeout(() => {
      gameState.setHostPlayingState((prev) => {
        if (!prev.isSubmitting || prev.answerSubmitted) return prev;
        console.warn('GameDisplay: submit timed out without server ack — unlocking');
        return { ...prev, isSubmitting: false };
      });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, [
    gameState.hostPlayingState.isSubmitting,
    gameState.hostPlayingState.answerSubmitted,
    gameState.setHostPlayingState,
  ]);
  
  // Socket events and live answers (use hook return as single source of truth for liveAnswers)
  const { liveAnswers } = useGameSocketEvents({
    socket,
    roomState,
    hostIsPlaying,
    myPlayerId,
    currentQuestion: gameState.currentQuestion,
    previousQuestionIdRef: gameState.previousQuestionIdRef,
    setCurrentQuestion: gameState.setCurrentQuestion,
    setTimeRemaining: gameState.setTimeRemaining,
    setQuestionResult: gameState.setQuestionResult,
    setShouldUpdateStandings: gameState.setShouldUpdateStandings,
    setLeaderboardFromResult: gameState.setLeaderboardFromResult,
    setGameEnded: gameState.setGameEnded,
    setFinalLeaderboard: gameState.setFinalLeaderboard,
    setGameStatistics: gameState.setGameStatistics,
    setWaitingForNewGame: gameState.setWaitingForNewGame,
    setShowHint: gameState.setShowHint,
    setHostPlayingState: gameState.setHostPlayingState,
    resetHostPlayingState: gameState.resetHostPlayingState,
    roomNameRef,
  });

  // Question flip animation
  const flipState = useQuestionFlip({
    currentQuestion: gameState.currentQuestion,
    liveAnswers,
    totalPlayers: roomState.players.length,
    answerSubmitted: gameState.hostPlayingState.answerSubmitted,
    hostIsPlaying,
  });

  // Timer management
  const { timeSeconds, progress } = useQuestionTimer({
    currentQuestion: gameState.currentQuestion,
    timeRemaining: gameState.timeRemaining,
    questionResult: gameState.questionResult,
    gameState: roomState.gameState,
    roomHasCurrentQuestion: !!roomState.currentQuestion,
    isPaused: roomState.isPaused,
    setTimeRemaining: gameState.setTimeRemaining,
  });

  // Trigger flip when timer runs out
  useTimerFlipTrigger(
    gameState.timeRemaining,
    gameState.currentQuestion,
    flipState.showQuestionFlip,
    flipState.setShowQuestionFlip,
    flipState.previousQuestionIdForFlipRef
  );

  // Player tracking for reconnections
  usePlayerTracking(roomState.players, gameState.currentQuestion);

  // roomState.players is synced from question-result leaderboard in useSocketEvents (App).
  const standingsPlayers = gameState.leaderboardFromResult
    ? syncPlayersFromLeaderboard(roomState.players, gameState.leaderboardFromResult)
    : roomState.players;

  // Handle answer submission when host is playing
  const handleSubmitAnswer = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!gameState.currentQuestion || 
        gameState.hostPlayingState.answerSubmitted ||
        gameState.hostPlayingState.isSubmitting ||
        isSubmittingRef.current ||
        !socket || !roomState) {
      return;
    }
    
    // Check if this is a pair matching game
    const isPairMatching = roomState.gameType === 'pair-matching' || roomState.gameType === 'team-pair-matching' || roomState.gameType === 'party-pair-matching' || roomState.gameType === 'family-pair-matching';
    
    // For pair matching, check if all pairs are selected
    if (isPairMatching) {
      const allPairsSelected = gameState.currentQuestion.leftItems && 
        gameState.currentQuestion.leftItems.every(
          leftItem => gameState.hostPlayingState.selectedPairs?.[leftItem] && 
          gameState.hostPlayingState.selectedPairs[leftItem] !== ''
        );
      if (!allPairsSelected) {
        return;
      }
    } else {
      // For other games, check selectedAnswer
      if (!gameState.hostPlayingState.selectedAnswer) {
        return;
      }
    }
    
    const questionId = gameState.currentQuestion.questionId || gameState.currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found', gameState.currentQuestion);
      return;
    }
    
    // Only lock the button (isSubmitting). Do NOT set answerSubmitted until answer-received —
    // otherwise a rejected submit (e.g. stale host player socket) hides Submit forever.
    isSubmittingRef.current = true;
    gameState.setHostPlayingState(prev => ({ ...prev, isSubmitting: true }));
    
    const clientTimestamp = Date.now();
    
    // For pair matching, send pairs as JSON string
    let answer: string;
    if (isPairMatching && gameState.hostPlayingState.selectedPairs) {
      answer = JSON.stringify(gameState.hostPlayingState.selectedPairs);
    } else {
      answer = gameState.hostPlayingState.selectedAnswer || '';
    }
    
    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer,
      timestamp: clientTimestamp,
    });
  };
  
  // Handle pair selection for pair matching (single update so one right item is never paired to two lefts)
  const handlePairSelect = (leftItem: string, rightItem: string) => {
    gameState.setHostPlayingState(prev => {
      const pairs = { ...prev.selectedPairs };
      if (rightItem) {
        for (const key of Object.keys(pairs)) {
          if (key !== leftItem && pairs[key] === rightItem) {
            pairs[key] = '';
          }
        }
      }
      pairs[leftItem] = rightItem || '';
      return { ...prev, selectedPairs: pairs };
    });
  };

  const handleRestartGame = () => {
    if (socket && roomState) {
      gameState.setWaitingForNewGame(true);
      gameState.setGameEnded(false);
      gameState.setFinalLeaderboard([]);
      gameState.setCurrentQuestion(null);
      gameState.setQuestionResult(null);
      gameState.resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  const handleBackToGameSelection = () => {
    if (onBackToGameSelection) {
      onBackToGameSelection();
    }
  };

  const handlePlaySameGame = () => {
    if (socket && roomState) {
      gameState.setGameEnded(false);
      gameState.setFinalLeaderboard([]);
      gameState.setGameStatistics(null);
      gameState.setCurrentQuestion(null);
      gameState.setQuestionResult(null);
      gameState.setWaitingForNewGame(true);
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  const handlePlayAnotherGame = () => {
    if (onBackToGameSelection) {
      onBackToGameSelection();
    } else {
      window.location.reload();
    }
  };

  // Early returns for different game states
  if (gameState.gameEnded) {
    return (
      <GameEndScreen
        finalLeaderboard={gameState.finalLeaderboard}
        gameStatistics={gameState.gameStatistics}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={handlePlayAnotherGame}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!gameState.currentQuestion && !gameState.waitingForNewGame && !gameState.gameEnded) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
        title={t('gameplay.loading.question')}
      />
    );
  }
  
  if (gameState.waitingForNewGame) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{t('gameplay.loading.startingNewGame')}</h1>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '1rem'
            }}></div>
            <p className="subtitle">{t('gameplay.loading.preparingQuestions')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState.currentQuestion) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
        title={t('gameplay.loading.question')}
      />
    );
  }

  return (
    <div className="app">
      {/* Header with back and refresh buttons */}
      {roomState.gameState === 'playing' && (
        <Header
          leftButton={{
            icon: HeaderIcons.back,
            onClick: handleBackToGameSelection,
            tooltip: t('gameplay.backToGameSelection')
          }}
          rightButton={{
            icon: HeaderIcons.refresh,
            onClick: handleRestartGame,
            tooltip: t('gameplay.restartGame')
          }}
          centerContent={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div className="header-game-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="game-header-label">{t('gameplay.partyHost.gameLabel')}</span>
                <span className="game-header-value">
                  {getGameName(roomState.gameType)}
                </span>
              </div>
              <div className="game-header-divider header-game-name" />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="game-header-label">{t('gameplay.partyHost.roomCodeLabel')}</span>
                <div style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 'bold', 
                  letterSpacing: '0.15rem',
                  color: 'var(--color-primary)',
                  padding: '0.3rem 0.8rem',
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
                  borderRadius: '6px',
                  border: '2px solid var(--color-primary)',
                }}>
                  {roomState.roomName}
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      {roomState.gameState === 'playing' && (
        <div className="game-host-layout">
          {/* Left side: Question and Answers (2/3 width). When host not playing, no answers section (host-only). */}
          <div className={`game-host-left ${!gameState.currentQuestion.isPicturePerfect || !gameState.currentQuestion.imageUrl ? 'no-image' : ''} ${!hostIsPlaying ? 'host-only' : ''}`}>
            {/* Timer section */}
            <div className="game-host-timer-section">
              <TimerSection
                questionNumber={gameState.currentQuestion.questionNumber}
                totalQuestions={gameState.currentQuestion.totalQuestions}
                timeSeconds={timeSeconds}
                progress={progress}
              />
            </div>
            
            {/* Image section */}
            {gameState.currentQuestion.isPicturePerfect && (gameState.currentQuestion.imageUrl || gameState.currentQuestion.fallbackImageUrl) && (
              <div className="game-host-image-section">
                <div className="game-image-container">
                  <ImageWithFallback
                    src={gameState.currentQuestion.imageUrl || ''}
                    fallbackSrc={gameState.currentQuestion.fallbackImageUrl}
                    alt={gameState.currentQuestion.isAnimalQuiz ? t('gameplay.imageAlt.animalQuiz') : t('gameplay.imageAlt.guessWho')}
                  />
                </div>
              </div>
            )}
            
            {/* Question section with flip animation */}
            <div className={`game-host-question-section${flipState.showQuestionFlip || flipState.isFadingOut ? ' game-host-question-section--showing-fact' : ''}`}>
              <QuestionDisplay
                question={gameState.currentQuestion}
                showQuestionFlip={flipState.showQuestionFlip}
                isFadingOut={flipState.isFadingOut}
                previousQuestionIdForFlipRef={flipState.previousQuestionIdForFlipRef}
                showHint={gameState.showHint}
                gameType={roomState.gameType}
              />
            </div>
            
            {/* Answers section – only when host is playing; when hosting only, space is used for larger image/question */}
            {hostIsPlaying && (
              <div className="game-host-answers-section">
                {/* Pair Matching: Show leftItems and rightItems in same style as answer options */}
                {(roomState.gameType === 'pair-matching' || roomState.gameType === 'team-pair-matching' || roomState.gameType === 'party-pair-matching' || roomState.gameType === 'family-pair-matching') &&
                 gameState.currentQuestion.leftItems && gameState.currentQuestion.rightItems ? (
                  <div className="options-list">
                    {gameState.currentQuestion.leftItems.map((item, index) => (
                      <div key={`left-${index}`} className="option-item">
                        {item}
                      </div>
                    ))}
                    {gameState.currentQuestion.rightItems.map((item, index) => (
                      <div key={`right-${index}`} className="option-item">
                        {item}
                      </div>
                    ))}
                  </div>
                ) : (
                  <AnswerOptions
                    question={gameState.currentQuestion}
                    selectedAnswer={gameState.hostPlayingState.selectedAnswer}
                    answerSubmitted={gameState.hostPlayingState.answerSubmitted}
                    hostIsPlaying={hostIsPlaying}
                    showHint={gameState.showHint}
                    gameType={roomState.gameType}
                    onAnswerSelect={(answer) => {
                      gameState.setHostPlayingState(prev => ({ ...prev, selectedAnswer: answer }));
                    }}
                    onSubmitAnswer={handleSubmitAnswer}
                    answerCorrect={gameState.hostPlayingState.answerCorrect}
                    waitingForOthers={gameState.hostPlayingState.waitingForOthers}
                    isSubmitting={gameState.hostPlayingState.isSubmitting}
                    selectedPairs={gameState.hostPlayingState.selectedPairs}
                    onPairSelect={handlePairSelect}
                  />
                )}
              </div>
            )}
          </div>
          
          {/* Right side: Standings and Live Answers (1/3 width) */}
          <div className="game-host-right">
            <StandingsSidebar
              players={standingsPlayers}
              liveAnswers={liveAnswers}
              totalPlayers={standingsPlayers.length}
              questionId={gameState.currentQuestion?.questionId || gameState.currentQuestion?.id}
              shouldUpdateScores={gameState.shouldUpdateStandings}
              mode={gameUsesTeams(roomState.gameType) ? 'teams' : 'players'}
              teams={roomState.teams}
              theme={{
                primary: 'var(--color-primary)',
                primaryLight: 'rgba(var(--color-primary-rgb), 0.15)',
                accent: 'var(--color-tertiary)',
              }}
            />          </div>
        </div>
      )}
    </div>
  );
};

export default GameDisplay;
